import { useEffect, useState } from "react";
import { analyticsAPI } from "../services/api";
import { useAuth } from "../context/AuthContext";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { Package, Users, Bike, TrendingUp, DollarSign, CheckCircle, Building2 } from "lucide-react";
import { format } from "date-fns";

const COLORS = ["#f5a623","#22c55e","#3b82f6","#a855f7","#ff6b35"];

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background:"var(--bg3)", border:"1px solid var(--border)", borderRadius:8, padding:"10px 14px" }}>
      <p style={{ color:"var(--text2)", fontSize:11, marginBottom:4 }}>{label}</p>
      {payload.map(p => (
        <p key={p.name} style={{ color: p.color, fontSize:13, fontWeight:600 }}>
          {p.name}: {p.name === "revenue" ? `GHS ${parseFloat(p.value).toFixed(2)}` : p.value}
        </p>
      ))}
    </div>
  );
};

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    analyticsAPI.get()
      .then(r => setData(r.data.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="loading-center"><div className="spinner" /><span>Loading analytics...</span></div>;
  if (!data) return <div className="empty-state"><p>Failed to load analytics</p></div>;

  const { orders, users, top_riders, service_breakdown, daily_orders } = data;

  const chartData = (daily_orders || []).map(d => ({
    date: format(new Date(d.date), "MMM dd"),
    orders: d.orders,
    revenue: parseFloat(d.revenue || 0),
  }));

  const pieData = (service_breakdown || []).map(s => ({
    name: s.service_type,
    value: parseInt(s.count),
  }));

  const deliveryRate = orders?.total_orders
    ? Math.round((orders.delivered / orders.total_orders) * 100)
    : 0;

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Dashboard</div>
          <div className="page-subtitle">
            {data?.scope === "branch" ? "Your branch overview" : "Live operations overview"}
          </div>
        </div>
      </div>

      {/* Branch manager scope notice */}
      {user?.role === "branch_manager" && (
        <div style={{
          display:"flex", alignItems:"center", gap:10,
          background:"rgba(168,85,247,0.08)", border:"1px solid rgba(168,85,247,0.25)",
          borderRadius:10, padding:"10px 16px", marginBottom:20, fontSize:13,
        }}>
          <Building2 size={16} style={{color:"#a855f7", flexShrink:0}}/>
          <span style={{color:"var(--text2)"}}>
            You are viewing data for <strong style={{color:"#a855f7"}}>your branch only</strong>.
            All stats, orders, riders and financials are scoped to your branch.
          </span>
        </div>
      )}

      {/* Stat cards */}
      <div className="stats-grid">
        <div className="stat-card accent">
          <div className="stat-label">Total Orders</div>
          <div className="stat-value">{orders?.total_orders?.toLocaleString() || 0}</div>
          <div className="stat-sub">Today: {orders?.today_orders || 0}</div>
          <Package size={40} className="stat-icon" />
        </div>
        <div className="stat-card green">
          <div className="stat-label">Revenue (GHS)</div>
          <div className="stat-value">{parseFloat(orders?.total_revenue || 0).toLocaleString("en-GH", { maximumFractionDigits: 0 })}</div>
          <div className="stat-sub">Today: GHS {parseFloat(orders?.today_revenue || 0).toFixed(2)}</div>
          <DollarSign size={40} className="stat-icon" />
        </div>
        <div className="stat-card blue">
          <div className="stat-label">Active Users</div>
          <div className="stat-value">{users?.total_users?.toLocaleString() || 0}</div>
          <div className="stat-sub">{users?.customers || 0} customers · {users?.riders || 0} riders</div>
          <Users size={40} className="stat-icon" />
        </div>
        <div className="stat-card green">
          <div className="stat-label">Delivery Rate</div>
          <div className="stat-value">{deliveryRate}%</div>
          <div className="stat-sub">{orders?.delivered || 0} delivered · {orders?.cancelled || 0} cancelled</div>
          <CheckCircle size={40} className="stat-icon" />
        </div>
      </div>

      {/* Charts */}
      <div className="dash-grid" style={{ marginBottom: 16 }}>
        <div className="card">
          <div className="card-header">
            <span className="card-title">Orders & Revenue (7 days)</span>
          </div>
          <div className="card-body">
            {chartData.length === 0 ? (
              <div className="empty-state"><p>No data yet</p></div>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="orders" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#f5a623" stopOpacity={0.3} />
                      <stop offset="100%" stopColor="#f5a623" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#22c55e" stopOpacity={0.3} />
                      <stop offset="100%" stopColor="#22c55e" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="date" tick={{ fill: "var(--text3)", fontSize: 11 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: "var(--text3)", fontSize: 11 }} axisLine={false} tickLine={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Area type="monotone" dataKey="orders" stroke="#f5a623" strokeWidth={2} fill="url(#orders)" name="orders" />
                  <Area type="monotone" dataKey="revenue" stroke="#22c55e" strokeWidth={2} fill="url(#rev)" name="revenue" />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <span className="card-title">Service Breakdown</span>
          </div>
          <div className="card-body">
            {pieData.length === 0 ? (
              <div className="empty-state"><p>No data yet</p></div>
            ) : (
              <>
                <ResponsiveContainer width="100%" height={160}>
                  <PieChart>
                    <Pie data={pieData} cx="50%" cy="50%" innerRadius={45} outerRadius={70} paddingAngle={3} dataKey="value">
                      {pieData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip contentStyle={{ background: "var(--bg3)", border: "1px solid var(--border)", borderRadius: 8 }} />
                  </PieChart>
                </ResponsiveContainer>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 8 }}>
                  {pieData.map((d, i) => (
                    <div key={d.name} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12 }}>
                      <span style={{ width: 8, height: 8, borderRadius: 2, background: COLORS[i % COLORS.length], display: "inline-block" }} />
                      <span style={{ color: "var(--text2)", textTransform: "capitalize" }}>{d.name}</span>
                      <span style={{ fontWeight: 700 }}>{d.value}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Top Riders */}
      <div className="card">
        <div className="card-header">
          <span className="card-title">🏆 Top Riders</span>
        </div>
        <div className="table-wrap">
          <table>
            <thead><tr>
              <th>#</th><th>Rider</th><th>Deliveries</th><th>Rating</th><th>Vehicle</th>
            </tr></thead>
            <tbody>
              {(top_riders || []).length === 0
                ? <tr><td colSpan={5}><div className="empty-state"><p>No data</p></div></td></tr>
                : (top_riders || []).map((r, i) => (
                  <tr key={i}>
                    <td style={{ color: "var(--text3)" }}>#{i + 1}</td>
                    <td className="fw-bold">{r.name}</td>
                    <td className="mono">{r.total_deliveries}</td>
                    <td>
                      <span style={{ color: "var(--accent)" }}>★ {parseFloat(r.rating || 0).toFixed(1)}</span>
                    </td>
                    <td style={{ textTransform: "capitalize", color: "var(--text2)" }}>{r.vehicle_type}</td>
                  </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
